// Modal de Resumo do Quiz
// Exibe estatísticas detalhadas do desempenho do membro no quiz diário

import React, { useState, useEffect, useCallback } from 'react';
import type { QuizSummary } from '../../utils/QuizSystem';

interface QuizSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName?: string;
}

export const QuizSummaryModal: React.FC<QuizSummaryModalProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName = 'Membro'
}) => {
  const [summary, setSummary] = useState<QuizSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Buscar tentativas do banco de dados
      const { supabase } = await import('../../lib/supabase');
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      
      const { data: attempts, error: attemptsError } = await supabase
        .from('quiz_answer_attempts' as any)
        .select('*')
        .eq('member_id', memberId)
        .gte('created_at', twentyFourHoursAgo)
        .order('created_at', { ascending: false });
      
      if (attemptsError) {
        console.error('❌ Erro ao buscar tentativas:', attemptsError);
        throw attemptsError;
      }
      
      // Calcular resumo a partir das tentativas
      const questionsAnswered = attempts?.length || 0;
      const correctAnswers = attempts?.filter((a: any) => a.is_correct).length || 0;
      
      // Buscar histórico de pontos de quiz nas últimas 24h em member_points_history
      console.log('🔍 [QuizSummary] Buscando pontos de quiz (member_points_history)', {
        memberId,
        twentyFourHoursAgo
      });

      const { data: quizPointHistory, error: quizPointError } = await supabase
        .from('member_points_history' as any)
        .select('*')
        .eq('member_id', memberId)
        .gte('changed_at', twentyFourHoursAgo)
        .or('change_reason.eq.quiz_completed,field_changed.eq.quiz_points')
        .order('changed_at', { ascending: false });

      if (quizPointError) {
        console.error('❌ [QuizSummary] Erro ao buscar pontos de quiz:', quizPointError);
        throw quizPointError;
      }

      const totalPoints = (quizPointHistory || []).reduce((sum: number, item: any) => {
        const difference = typeof item.difference === 'number' ? item.difference : 0;
        return sum + difference;
      }, 0);

      console.log('💰 [QuizSummary] Pontos de quiz capturados:', {
        count: quizPointHistory?.length || 0,
        events: quizPointHistory,
        totalPoints
      });
      
      const accuracy = questionsAnswered > 0 ? (correctAnswers / questionsAnswered) * 100 : 0;
      
      setSummary({
        date: new Date().toISOString().split('T')[0],
        questionsAnswered,
        correctAnswers,
        totalPoints,
        accuracy,
        categories: {}
      });
      
      console.log('📊 [QuizSummary] Resumo carregado do banco:', {
        questionsAnswered,
        correctAnswers,
        totalPoints,
        accuracy
      });
    } catch (err) {
      setError('Erro ao carregar resumo do quiz');
      console.error('❌ [QuizSummary] Erro ao carregar resumo:', err);
    } finally {
      setIsLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (isOpen) {
      void loadSummary();
    }
  }, [isOpen, loadSummary]);

  const getPerformanceColor = (accuracy: number) => {
    if (accuracy >= 80) return 'text-green-600';
    if (accuracy >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceMessage = (accuracy: number) => {
    if (accuracy === 100) return 'Perfeito! 🎉';
    if (accuracy >= 80) return 'Excelente! 👏';
    if (accuracy >= 60) return 'Bom trabalho! 👍';
    return 'Continue praticando! 💪';
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'princípios básicos':
        return '📚';
      case 'medicamentos':
        return '💊';
      case 'potências':
        return '⚗️';
      case 'história':
        return '📜';
      default:
        return '❓';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Resumo do Quiz</h2>
              <p className="text-green-100 mt-1">{memberName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Carregando resumo...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={loadSummary}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Tentar Novamente
              </button>
            </div>
          ) : summary ? (
            <>
              {/* Estatísticas principais */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{summary.questionsAnswered}</div>
                  <div className="text-sm text-blue-800">Perguntas</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{summary.correctAnswers}</div>
                  <div className="text-sm text-green-800">Acertos</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-600">{summary.totalPoints}</div>
                  <div className="text-sm text-purple-800">Pontos</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg text-center">
                  <div className={`text-2xl font-bold ${getPerformanceColor(summary.accuracy)}`}>
                    {Math.round(summary.accuracy)}%
                  </div>
                  <div className="text-sm text-yellow-800">Precisão</div>
                </div>
              </div>

              {/* Mensagem de desempenho */}
              <div className="text-center mb-6">
                <div className="text-3xl mb-2">{getPerformanceMessage(summary.accuracy).split(' ')[1]}</div>
                <h3 className={`text-xl font-semibold ${getPerformanceColor(summary.accuracy)}`}>
                  {getPerformanceMessage(summary.accuracy).split(' ')[0]}
                </h3>
                <p className="text-gray-600 mt-2">
                  Você respondeu {summary.questionsAnswered} pergunta{summary.questionsAnswered !== 1 ? 's' : ''} 
                  {summary.questionsAnswered > 0 && ` e acertou ${summary.correctAnswers}`}
                </p>
              </div>

              {/* Desempenho por categoria */}
              {summary?.categories && Object.keys(summary.categories).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Desempenho por Categoria</h4>
                  <div className="space-y-3">
                    {Object.entries(summary.categories).map(([category, stats]) => {
                      const categoryAccuracy = stats.answered > 0 ? (stats.correct / stats.answered) * 100 : 0;
                      return (
                        <div key={category} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <span className="text-xl mr-2">{getCategoryIcon(category)}</span>
                              <span className="font-medium text-gray-800 capitalize">{category}</span>
                            </div>
                            <span className={`font-semibold ${getPerformanceColor(categoryAccuracy)}`}>
                              {Math.round(categoryAccuracy)}%
                            </span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <span>{stats.correct}/{stats.answered} acertos</span>
                            <div className="flex-1 mx-3 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  categoryAccuracy >= 80 ? 'bg-green-500' :
                                  categoryAccuracy >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${categoryAccuracy}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Motivação e próximos passos */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">💡 Dica do Dia</h4>
                <p className="text-gray-700 text-sm">
                  {summary.accuracy === 100 
                    ? "Parabéns pelo desempenho perfeito! Continue estudando para manter esse nível de excelência."
                    : summary.accuracy >= 80
                    ? "Excelente trabalho! Revise os conceitos das perguntas que errou para aprimorar ainda mais seu conhecimento."
                    : summary.accuracy >= 60
                    ? "Bom progresso! Dedique um tempo extra ao estudo dos temas que apresentaram mais dificuldade."
                    : "Continue praticando! A homeopatia é uma ciência rica em detalhes. Cada quiz é uma oportunidade de aprender mais."
                  }
                </p>
              </div>

              {/* Informações adicionais */}
              <div className="text-center text-sm text-gray-500 mb-4">
                <p>Quiz realizado em {new Date().toLocaleDateString('pt-BR')}</p>
                <p className="mt-1">Novos quizzes estarão disponíveis amanhã!</p>
              </div>

              {/* Botão de fechar */}
              <div className="text-center">
                <button
                  onClick={onClose}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Fechar Resumo
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600">Nenhum quiz foi realizado hoje ainda.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizSummaryModal;